import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DateRangeText, SelectDateRangeModal } from '@builder';

import { Box, Dropdown, DropdownItem, DropdownVariant } from '@base-app';

import { useModal } from '@components/hooks';
import {
  clearTimeFromDate,
  getFirstDayOfMonth,
  getFirstDayOfPreviousMonth,
  getFirstDayOfWeek,
  getFirstDayOfYear,
  getLastDayOfMonth,
  getLastDayOfPreviousMonth,
  getLastDayOfWeek,
  getLastDayOfYear,
  getFirstDayOfQuarter,
  getLastDayOfQuarter,
} from '@common/utils';
import { DateRangeDropdownProps, DateRangeOptionId } from './DateRangeDropdown.types';
import { LayoutOneDateRangeOptionId } from '../../../../../modules/study-insights/components/ParticipantRecluitment/Layouts/LayoutOne/LayoutOne.types';
import { LayoutThreeDateRangeOptionId } from '../../../../../modules/study-insights/components/ParticipantRecluitment/Layouts/LayoutThree/LayoutThree.types';
import { StyledDateRangeDropdownContainer } from './DateRangeDropdown.styles';
import { SelectDateRangeSubmitValues } from '../SelectDateRangeModal/SelectDateRangeModal.types';
import { COMMON_TRANSLATION, LAYOUT_ONE } from '@/common/constants';

const DateRangeDropdown: FC<DateRangeDropdownProps> = ({
  buttonStyles = {},
  css,
  dateRangeErrorDescription,
  dateRangeErrorTitle,
  maxDate,
  minDate,
  onSelect,
  showText = false,
  value,
  layout,
}) => {
  const { t } = useTranslation(COMMON_TRANSLATION);
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOptionId>();
  const { isShowing, toggle } = useModal();

  const handleSelectDateRange = (item: DropdownItem): void => {
    const { id } = item;
    // not set option for Custom option until user set dates.
    if (id !== DateRangeOptionId.CUSTOM) {
      setSelectedDateRange(id as DateRangeOptionId);
    }

    switch (id) {
      case DateRangeOptionId.THIS_WEEK: {
        setStartDate(getFirstDayOfWeek());
        setEndDate(getLastDayOfWeek());
        break;
      }
      case DateRangeOptionId.THIS_MONTH: {
        setStartDate(getFirstDayOfMonth());
        setEndDate(getLastDayOfMonth());
        break;
      }
      case DateRangeOptionId.LAST_MONTH: {
        setStartDate(getFirstDayOfPreviousMonth());
        setEndDate(getLastDayOfPreviousMonth());
        break;
      }
      case DateRangeOptionId.ALL_TIME: {
        setStartDate(minDate);
        setEndDate(undefined);
        break;
      }
      case DateRangeOptionId.CUSTOM: {
        // open Select Custom Date Range modal
        toggle();
        break;
      }
      case LayoutOneDateRangeOptionId.THIS_QUARTER: {
        const today = new Date();
        const quarter = Math.floor(today.getMonth() / 3);
        const start = new Date(today.getFullYear(), quarter * 3, 1);
        const end = new Date(start.getFullYear(), start.getMonth() + 3, 0);
        setStartDate(start);
        setEndDate(end);
        break;
      }
      case LayoutOneDateRangeOptionId.LAST_QUARTER: {
        const today = new Date();
        const quarter = Math.floor(today.getMonth() / 3);
        const start = new Date(today.getFullYear(), (quarter - 1) * 3, 1);
        const end = new Date(start.getFullYear(), start.getMonth() + 3, 0);
        setStartDate(start);
        setEndDate(end);
        break;
      }
      case LayoutOneDateRangeOptionId.THIS_YEAR: {
        const start = getFirstDayOfYear();
        const end = getLastDayOfYear();
        setStartDate(start);
        setEndDate(end);
        break;
      }
      case LayoutOneDateRangeOptionId.LAST_YEAR: {
        const today = new Date();
        const lastYear = today.getFullYear() - 1;
        const start = new Date(lastYear, 0, 1);
        const end = new Date(lastYear, 11, 31);
        setStartDate(start);
        setEndDate(end);
        break;
      }
    }
  };

  const handleSubmitSelectDateRangeModal = ({ fromDate, toDate }: SelectDateRangeSubmitValues): void => {
    // close modal
    toggle();

    // set custom date range
    setStartDate(clearTimeFromDate(new Date(fromDate)));
    setEndDate(clearTimeFromDate(new Date(toDate)));
    setSelectedDateRange(DateRangeOptionId.CUSTOM);
  };

  useEffect(() => {
    if (!selectedDateRange) {
      return;
    }

    onSelect?.({
      startDate,
      endDate,
      selectedDateRange,
    });
  }, [startDate, endDate, selectedDateRange]);

  useEffect(() => {
    if (value !== undefined) {
      setStartDate(value.startDate ? clearTimeFromDate(new Date(value.startDate)) : undefined);
      setEndDate(value.endDate ? clearTimeFromDate(new Date(value.endDate)) : undefined);
      setSelectedDateRange(value.selectedDateRange);
    } else if (layout === LAYOUT_ONE) {
      setStartDate(getFirstDayOfQuarter());
      setEndDate(getLastDayOfQuarter());
      setSelectedDateRange(DateRangeOptionId.THIS_QUARTER);
    } else {
      setStartDate(getFirstDayOfMonth());
      setEndDate(getLastDayOfMonth());
      setSelectedDateRange(DateRangeOptionId.THIS_MONTH);
    }
  }, []);

  let options: DropdownItem[];
  if (layout === LAYOUT_ONE) {
    options = [...Object.values(LayoutOneDateRangeOptionId)].map((value) => ({
      id: value,
      label: t(`date_range_dropdown_options.${value}`),
    }));
  } else {
    options = [...Object.values(LayoutThreeDateRangeOptionId)].map((value) => ({
      id: value,
      label: t(`date_range_dropdown_options.${value}`),
    }));
  }

  return (
    <StyledDateRangeDropdownContainer>
      {showText && (
        <Box css={{ paddingRight: '$4' }}>
          <DateRangeText startDate={startDate} endDate={endDate} />
        </Box>
      )}
      {selectedDateRange && (
        <Dropdown
          buttonStyles={buttonStyles}
          css={{ ...css }}
          id="dropdown-date-range"
          items={options}
          onSelect={handleSelectDateRange}
          title={t(`date_range_dropdown_options.${selectedDateRange}`)}
          variant={DropdownVariant.PILL}
        />
      )}
      {isShowing && (
        <SelectDateRangeModal
          dateRangeErrorDescription={dateRangeErrorDescription}
          dateRangeErrorTitle={dateRangeErrorTitle}
          isOpen={isShowing}
          maxDate={maxDate}
          minDate={minDate}
          onSubmit={handleSubmitSelectDateRangeModal}
          toggle={toggle}
          startDate={startDate}
          endDate={endDate}
        />
      )}
    </StyledDateRangeDropdownContainer>
  );
};

export default DateRangeDropdown;
